import { AudioPlayer } from '@c/Ui'
import React, { useEffect, useRef, useState } from 'react'

import { IMapObject } from '@/core/interface'
import { useAppDispatch } from '@/core/store'
import { setSelectedInterestPoint } from '@/core/store/catalog.store'
import { setFullImageView } from '@/core/store/ui.store'

import {
  InfoWindowControlSection,
  InfoWindowExpandedSection,
  InfoWindowGallerySection,
  InfoWindowHeader,
  InfoWindowSharedSection,
} from './index'

interface IMapProps {
  data: IMapObject | null
  onClose: () => void
  active: boolean
  excursionStart: boolean
  currentStep: number
  onCloseRoute: () => void
  setCurrentStep: (step: number) => void
  pointsLength: number | undefined
}

export const InfoWindow: React.FC<IMapProps> = ({
  data,
  onClose,
  active,
  excursionStart,
  currentStep,
  onCloseRoute,
  setCurrentStep,
  pointsLength,
}) => {
  const dispatch = useAppDispatch()

  const [mode, setMode] = useState<'low' | 'middle' | 'high'>('middle')
  const [isOverflowing, setIsOverflowing] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const descrRef = useRef<HTMLParagraphElement>(null)

  const shareUrl = data?.xid ? `${window.location.origin}/map?interestPoint=${data.xid}` : null

  useEffect(() => {
    const el = descrRef.current
    if (!el) return

    const apply = () => {
      // Сброс
      el.style.display = ''
      // @ts-ignore
      el.style.webkitLineClamp = ''
      // @ts-ignore
      el.style.webkitBoxOrient = ''
      el.style.overflow = 'visible'
      el.style.overflowY = ''
      el.style.maxHeight = ''

      // форсим reflow
      void el.offsetHeight

      const cs = getComputedStyle(el)
      const lineHeight = parseFloat(cs.lineHeight || '0') || 1
      const actualHeight = el.scrollHeight
      const lines = actualHeight / lineHeight
      const over = lines > 4.1
      setIsOverflowing(over);

      if (!isExpanded && over) {
        // СВЕРНУТО: 4 строки
        el.style.display = '-webkit-box'
        // @ts-ignore
        el.style.webkitLineClamp = '4'
        // @ts-ignore
        el.style.webkitBoxOrient = 'vertical'
        el.style.overflow = 'hidden'
        el.style.maxHeight = `${lineHeight * 4}px`
        return;
      }

      // РАЗВЕРНУТО
      if (isExpanded) {
        if (lines > 6) {
          // >6 строк: ограничиваем 6 строками и даем скролл
          el.style.display = 'block'
          el.style.maxHeight = `${lineHeight * 6}px`
          el.style.overflowY = 'auto'
          el.style.overflowX = 'hidden';
          // для iOS
          (el.style as any).webkitOverflowScrolling = 'touch'
        } else {
          // ≤6 строк: показываем полностью
          el.style.display = 'block'
          el.style.maxHeight = `${actualHeight}px`
          el.style.overflow = 'visible'
        }
      }
    };

    apply();

    const ro = new ResizeObserver(apply)
    ro.observe(el);
    window.addEventListener('resize', apply)
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', apply)
    };
  }, [data, mode, isExpanded])

  function onPreviewClick() {
    if (!data?.gallery || data.gallery.length === 0) {
      return dispatch(setFullImageView(null))
    }

    const validGallery = data.gallery.filter(item => item.uri && item.uri.trim() !== '')

    if (validGallery.length === 0) {
      return dispatch(setFullImageView(null))
    }

    dispatch(
      setFullImageView({
        gallery: validGallery,
        description: data.info?.descr || '',
      }),
    )
  }

  function closeObject() {
    onClose()
    setMode('middle')
    dispatch(setSelectedInterestPoint(null))
  }

  function openFullViewImage() {
    const image = data?.preview?.source
      ? { gallery: [{ order: 0, uri: data.preview.source, caption: undefined, copyright: undefined }], description: data.info?.descr || '' }
      : null
    dispatch(setFullImageView(image))
  }

  return (
    <>
      <div
        className={`select_object ${active ? 'active' : ''} ${mode}`}
      >
        <InfoWindowHeader
          mode={mode}
          currentStep={currentStep}
          name={data?.name}
          closeObject={closeObject}
          excursionStart={excursionStart}
          setMode={setMode}
        />
        <InfoWindowGallerySection
          data={data}
          openFullViewImage={openFullViewImage}
          onPreviewClick={onPreviewClick}
          mode={mode}
        />
        {data && (
          <>
            {data.audioFile && (
              <AudioPlayer
                type={1}
                id={data.xid}
                file={data.audioFile}
                autoPlaying={false}
                excursion={'y'}
              />
            )}
            {mode !== 'low' && (
              <InfoWindowExpandedSection
                isOverflowing={isOverflowing}
                descrRef={descrRef}
                description={data?.info?.descr}
                isExpanded={isExpanded}
                setIsExpanded={setIsExpanded}
              />
            )}
            {mode !== 'low' && shareUrl && <InfoWindowSharedSection shareUrl={shareUrl} />}
          </>
        )}
        {excursionStart && (
          <InfoWindowControlSection
            currentStep={currentStep}
            pointsLength={pointsLength}
            onCloseRoute={onCloseRoute}
            setCurrentStep={setCurrentStep}
          />
        )}
      </div>
    </>
  )
}
